import { create } from 'zustand';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useCanvasStore, type Item, type ItemType } from '@/stores/canvasStore';
import { parseSocialUrl, isPullablePost, type SocialLinkInfo } from '@/lib/socialLinks';
import { readFunctionError, watchExtensionJob } from '../jobs';
import { creativeScoutManifest } from './manifest';

// "Pull the full post?" after pasting a social post: the prompt's state and
// the analyze_post run it starts. The prompt itself is PullPostPrompt.tsx.

export const PULL_POST_ACTION = creativeScoutManifest.actions.find((a) => a.id === 'analyze_post')!;

const NEVER_ASK_PREFIX = 'mindfull.pullPost.neverAsk.';

/** "Don't ask again for this platform" lives on this device. */
export function isPullPromptMuted(platform: string): boolean {
  try {
    return localStorage.getItem(NEVER_ASK_PREFIX + platform) === '1';
  } catch {
    return false;
  }
}

export function mutePullPrompt(platform: string): void {
  try {
    localStorage.setItem(NEVER_ASK_PREFIX + platform, '1');
  } catch {
    // storage blocked: the prompt just keeps asking
  }
}

export interface PullPromptTarget {
  itemId: string;
  url: string;
  social: SocialLinkInfo;
}

interface PullPostState {
  /** The card the prompt is anchored to. */
  prompt: PullPromptTarget | null;
  /** Cards with a pull in flight. */
  pulling: Record<string, true>;
  offer: (target: PullPromptTarget) => void;
  dismiss: () => void;
  setPulling: (itemId: string, on: boolean) => void;
}

export const usePullPostStore = create<PullPostState>((set) => ({
  prompt: null,
  pulling: {},
  offer: (target) => set({ prompt: target }),
  dismiss: () => set({ prompt: null }),
  setPulling: (itemId, on) =>
    set((s) => {
      const pulling = { ...s.pulling };
      if (on) pulling[itemId] = true;
      else delete pulling[itemId];
      return { pulling };
    }),
}));

/**
 * Offer to pull a freshly added link card when it is a single post Top
 * Creatives can scrape and the user has not muted the platform.
 */
export function offerPullPost(item: Pick<Item, 'id' | 'item_type' | 'content'> | null | undefined): void {
  if (!item || item.item_type !== 'link') return;
  const url = (item.content as { url?: unknown } | null)?.url;
  if (typeof url !== 'string') return;
  const social = parseSocialUrl(url);
  if (!isPullablePost(social) || isPullPromptMuted(social.platform)) return;
  usePullPostStore.getState().offer({ itemId: item.id, url, social });
}

/** Put the server's copy of an item into the canvas store, replacing the local one. */
function replaceItem(row: Item): void {
  const typed = { ...row, item_type: row.item_type as ItemType };
  useCanvasStore.setState((s) => ({
    items: s.items.map((i) => (i.id === typed.id ? typed : i)),
    allItems: s.allItems.map((i) => (i.id === typed.id ? typed : i)),
  }));
}

/**
 * Run analyze_post on a link card and upgrade it in place when the job
 * finishes. The link card stays as it is if anything fails.
 */
export async function pullFullPost(target: PullPromptTarget, opts: { intervalMs?: number } = {}): Promise<boolean> {
  const { itemId, url, social } = target;
  const store = usePullPostStore.getState();
  store.setPulling(itemId, true);
  try {
    const { data, error } = await supabase.functions.invoke(PULL_POST_ACTION.function, {
      body: { action: PULL_POST_ACTION.id, url, item_id: itemId },
    });
    if (error) {
      const { message } = await readFunctionError(error);
      toast.error(message);
      return false;
    }
    const jobId = (data as { extension_job_id?: string } | null)?.extension_job_id;
    if (!jobId) {
      toast.error('Could not start the pull. Please try again.');
      return false;
    }

    const job = await watchExtensionJob(jobId, { intervalMs: opts.intervalMs ?? 3000, timeoutMs: 4 * 60 * 1000 });
    if (!job) {
      toast.info(`Still pulling the ${social.platformName} post. It will appear when you next open this board.`);
      return false;
    }
    if (job.status === 'failed') {
      toast.error(`Couldn't pull the ${social.platformName} post: ${job.error_message || 'unknown error'}. The link stays on your board.`);
      return false;
    }

    const finalId = (job.result?.item_id as string | undefined) ?? itemId;
    const { data: row } = await supabase.from('items').select('*').eq('id', finalId).maybeSingle();
    if (row) replaceItem(row as unknown as Item);
    return true;
  } catch (err) {
    console.error('[pullFullPost] failed:', err);
    toast.error('Could not pull the post. Please try again.');
    return false;
  } finally {
    usePullPostStore.getState().setPulling(itemId, false);
  }
}
